import { useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useIsMobile } from 'hooks/responsive';
import { v4 as uuidv4 } from 'uuid';
import AnimatedDrawerViewComponent from './components/AnimatedDrawerViewComponent';
import DefaultContentRenderer from './components/DefaultContentRenderer';
import DefaultHeaderRenderer from './components/DefaultHeaderRenderer';
import { closeAllDrawers, closeDrawer, useDrawerStore, useIsDrawerOpen } from './hooks/useDrawer';
import { useRootContainerReferenceStore } from './hooks/useRootContainerReferenceStore';
import type { DrawerViewProps } from './shared/types';

/**
 *
 * @param id - id of the drawer view is required, will be used as an identifier
 * @returns React.ReactPortal
 */
export default function DrawerView(props: DrawerViewProps) {
    const {
        id,
        position,
        headerRenderer = (headerProps) => <DefaultHeaderRenderer {...headerProps} />,
        contentRenderer = (contentProps) => <DefaultContentRenderer {...contentProps} />,
        footerRenderer,
        children,
        animate = true,
        ...restProps
    } = props;

    const isOpen = useIsDrawerOpen(id);
    const { drawerInstances } = useDrawerStore();
    const shouldAnimate = drawerInstances.length === 0 ? true : animate;
    const isMobile = useIsMobile();
    const rootContainerReference = useRootContainerReferenceStore((state) => state.ref);
    const deviceInstanceId = useMemo(() => uuidv4(), [isMobile]); // used to generate a new id when device size changes, the animatedDrawerView will be remounted when key changes
    const closeCurrent = useCallback(() => closeDrawer(id), [id]);

    /* Renderer Props */
    const headerProps = { closeAll: closeAllDrawers, closeCurrent };
    const footerProps = { closeAll: closeAllDrawers, closeCurrent };

    const fullscreen = (position !== 'center' && props.fullscreen && isMobile) || false;
    const mobilePosition = isMobile ? 'bottom' : position;
    const finalPosition = fullscreen ? position : mobilePosition;
    const resolvedSize = position === 'center' ? props.size : undefined; // size only exists on position center because modals fixed sizes

    if (!rootContainerReference) return null;

    return ReactDOM.createPortal(
        <AnimatedDrawerViewComponent
            id={id}
            key={deviceInstanceId}
            isOpen={isOpen}
            animate={shouldAnimate}
            position={finalPosition}
            fullscreen={fullscreen}
            size={resolvedSize}
            aria-modal
            tabIndex={-1}
            {...restProps}
        >
            {headerRenderer?.(headerProps)}
            {contentRenderer?.({ children })}
            {footerRenderer?.(footerProps)}
        </AnimatedDrawerViewComponent>,
        rootContainerReference
    );
}
